import type { ProductJourney, ProductProperties } from "@shared/analytics/productEvents";
import { BigDecimal } from "@solomon-labs/bigdecimal";

const symbols = new Set(["USDv", "USDV", "USDC", "USDT", "USDG", "PYUSD", "SOL", "WSOL"]);
export function swapToken(symbol: unknown): string {
    return typeof symbol === "string" && symbols.has(symbol) ? symbol : "other";
}

export function swapAmountBucket(input: unknown): ProductProperties["amount_bucket"] {
    try {
        const value = new BigDecimal(typeof input === "string" && input ? input : "0");
        if (!value.gt(0)) return "zero";
        if (value.lt(1)) return "under_1";
        if (value.lt(10)) return "1_to_10";
        if (value.lt(100)) return "10_to_100";
        if (value.lt(1000)) return "100_to_1000";
        return "1000_plus";
    } catch {
        return "zero";
    }
}

/** Adapts existing widget controls without exporting SDK dependencies to demo consumers. */
export function swapControlCapture(
    journey: ProductJourney
): (event: string, properties?: Record<string, unknown>) => void {
    return (event, values = {}): void => {
        const properties: ProductProperties = {
            mode: values.mode === "sell" ? "sell" : "buy",
            side: values.field === "receive" ? "receive" : "send"
        };
        switch (event) {
            case "swap_tab_viewed":
                journey.changed("form_viewed", properties);
                break;
            case "gate_blocked":
                journey.changed("blocked", { reason: "access_denied" });
                break;
            case "swap_token_picker_opened":
                journey.send("token_picker_opened", properties);
                break;
            case "swap_token_picker_closed":
                journey.send("token_picker_closed", properties);
                break;
            case "swap_token_selected":
                journey.send("token_selected", { ...properties, target_token: swapToken(values.to_symbol) });
                break;
            case "swap_amount_first_entered":
                journey.send("amount_updated", { ...properties, control: "input" });
                break;
            case "swap_max_clicked":
                journey.send("amount_updated", { ...properties, control: "max" });
                break;
            case "swap_mode_switched":
                journey.send("direction_changed", { mode: values.to_mode === "sell" ? "sell" : "buy" });
                break;
            case "swap_quote_retry_clicked":
                journey.send("retry_clicked", { control: "quote" });
                break;
            case "swap_quote_received":
                journey.send("quote_received", {
                    ...properties,
                    source_token: swapToken(values.send_symbol),
                    target_token: swapToken(values.receive_symbol),
                    amount_bucket: swapAmountBucket(values.send_amount),
                    duration_ms: typeof values.quote_latency_ms === "number" ? values.quote_latency_ms : undefined
                });
                break;
            case "swap_quote_failed":
                journey.send("quote_failed", {
                    ...properties,
                    error_code: typeof values.error_code === "string" ? values.error_code : "unknown"
                });
                break;
        }
    };
}
